import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Calendar01Icon,
  Mortarboard01Icon,
  TaskDone01Icon,
} from '@hugeicons/core-free-icons'

import { OverviewCard } from '@/components/common/overview-card'
import { Skeleton } from '@/components/ui/skeleton'
import { TrajectoryPathVisualization } from '@/features/trajectory-paths'
import {
  ConvexPositionProgressChart,
  ConvexPositionRadarChart,
} from '@/features/users'
import { useMyDashboardData } from '@/features/users/services/users.convex'

const searchSchema = z.object({
  redirect: z.string().startsWith('/').optional(),
})

export const Route = createFileRoute('/(dashboard)/')({
  component: RouteComponent,
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    if (search.redirect) {
      throw redirect({ to: search.redirect })
    }
    return { title: 'Overview' }
  },
})

function RouteComponent() {
  const { data, isLoading } = useMyDashboardData()

  const trajectorySteps = data?.trajectory.steps
  const trajectoryName = data?.trajectory.name

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            My Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your career progress and assessment insights
          </p>
        </div>
      </div>
      {/* Overview Cards Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-4 2xl:gap-7.5">
        {/* Current Position Level */}
        {isLoading ? (
          <Skeleton className="h-32" />
        ) : (
          <OverviewCard
            title="Current position level"
            total={data?.positionLevel ?? 'N/A'}
            icon={
              <HugeiconsIcon
                icon={Mortarboard01Icon}
                className="h-6 w-6 text-primary"
                strokeWidth={1.5}
              />
            }
          />
        )}

        {/* Career Path Trajectory */}
        <div className="col-span-1 md:col-span-2 h-full">
          {isLoading ? (
            <Skeleton className="h-full" />
          ) : trajectorySteps && trajectorySteps.length > 0 ? (
            <TrajectoryPathVisualization
              trajectoryPath={trajectorySteps}
              pathName={trajectoryName ?? ''}
              currentLevel={data?.positionLevel}
            />
          ) : (
            <div className="flex h-full p-6 bg-muted/30 rounded-lg items-center justify-center flex-col gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <HugeiconsIcon
                  icon={TaskDone01Icon}
                  className="h-6 w-6 text-primary"
                />
              </div>
              <span className="text-muted-foreground">
                No trajectory data available
              </span>
            </div>
          )}
        </div>

        {/* At Position Since */}
        {isLoading ? (
          <Skeleton className="h-32" />
        ) : (
          <OverviewCard
            title="At position since"
            total={
              data?.sinceDate
                ? new Date(data.sinceDate).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })
                : 'N/A'
            }
            icon={
              <HugeiconsIcon
                icon={Calendar01Icon}
                className="h-6 w-6 text-primary"
                strokeWidth={1.5}
              />
            }
          />
        )}
      </div>

      {/* Assessment Charts */}
      <div className="grid grid-cols-12 gap-4 md:gap-6 2xl:mt-7.5 2xl:gap-7.5">
        {/* Position Progress Chart - 2/3 width (8 columns) */}
        <div className="col-span-12 lg:col-span-8">
          <ConvexPositionProgressChart />
        </div>

        {/* Radar Chart - 1/3 width (4 columns) */}
        <div className="col-span-12 lg:col-span-4">
          <ConvexPositionRadarChart />
        </div>
      </div>
    </div>
  )
}
